import { ConfirmModal } from "./ConfirmModal";
import { useState } from "react";
import type { ChangelogEntry } from "../types";

interface Props {
  entries: ChangelogEntry[];
  isSysadmin: boolean;
  onClose: () => void;
  onCreated: (entry: ChangelogEntry) => void;
  onDeleted: (id: string) => void;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric", timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

export function ChangelogModal({ entries, isSysadmin, onClose, onCreated, onDeleted }: Props) {
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!title.trim() || !body.trim()) {
      setError("título e texto são obrigatórios");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch("/api/admin/changelog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body }),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao publicar");
        return;
      }
      onCreated(j as ChangelogEntry);
      setTitle("");
      setBody("");
      setCreating(false);
    } finally {
      setSaving(false);
    }
  };

  const [toDelete, setToDelete] = useState<ChangelogEntry | null>(null);
  const remove = async (entry: ChangelogEntry) => {
    try {
      await fetch(`/api/admin/changelog/${encodeURIComponent(entry.id)}`, { method: "DELETE" });
    } catch {
      /* ignore */
    }
    onDeleted(entry.id);
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal prompt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Novidades">
        <header className="modal-header">
          <h2>Novidades</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        {isSysadmin && (
          <div className="prompt-modal-toolbar">
            {!creating && (
              <button type="button" className="action-btn active" onClick={() => setCreating(true)}>
                + Publicar novidade
              </button>
            )}
          </div>
        )}

        {creating && (
          <div className="prompt-form">
            <input
              className="prompt-form-input"
              placeholder="Título (ex.: Resumo automático de conversas)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              spellCheck={false}
            />
            <textarea
              className="prompt-form-textarea"
              placeholder="O que mudou…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
            {error && <p className="prompt-form-error">{error}</p>}
            <div className="prompt-form-actions">
              <button type="button" className="action-btn" onClick={() => setCreating(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="action-btn active" onClick={() => void create()} disabled={saving}>
                {saving ? "Publicando…" : "Publicar"}
              </button>
            </div>
          </div>
        )}

        <div className="changelog-list">
          {entries.length === 0 ? (
            <p className="prompt-list-status">Nenhuma novidade publicada ainda.</p>
          ) : (
            entries.map((e) => (
              <div key={e.id} className="changelog-entry">
                <div className="changelog-entry-header">
                  <span className="changelog-entry-title">{e.title}</span>
                  <span className="changelog-entry-date">{fmtDate(e.publishedAt)}</span>
                  {isSysadmin && (
                    <button type="button" className="session-tool" onClick={() => setToDelete(e)} title="Apagar">
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                        <path d="M3 4h10M6.5 4V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M5 4l.5 9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1l.5-9" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="changelog-entry-body">{e.body}</p>
              </div>
            ))
          )}
        </div>
      </div>
      {toDelete && (
        <ConfirmModal
          title="Apagar entrada"
          message={`Apagar a entrada "${toDelete.title}"?`}
          confirmLabel="Apagar"
          onConfirm={() => void remove(toDelete)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}
